import { setRegisteredData } from "./registered-data";
import { armQuestionnaire, clearQuestionnaire } from "./questionnaire";
import { url } from "../../base-path";
import {
    startNinjaCut,
    teardownNinjaCut,
    getNinjaCutScore,
    preloadNinjaCutAssets,
} from "../../../games/ninja-cut/ninja-cut";

/**
 * In-modal navigation for the multi-step application flow (Ninja Cut).
 *
 * Screens live in #modal-form-application as .modal-screen[data-screen="..."] and are
 * shown one at a time. Navigation is driven by data attributes on buttons:
 *   - data-modal-next="<screen>"   → simply show that screen
 *   - data-modal-action="<action>" → run a behaviour (start/finish game, answer, decline)
 *
 * Flow: confirmation → instr-1 → start → game → result (or timeout-result) → quest → thanks.
 * The trial (demo) game can be played UP TO THREE times: start → (start-demo) game → demo-result → game.
 *
 * `thanks` is a dead end, so it doubles as the trigger for the survey overlay — a SECOND modal
 * stacked on top of this one a moment later (see ./questionnaire).
 */

const MODAL_SELECTOR = '#modal-form-application';
const MAX_DEMO_ATTEMPTS = 3;
// The game must be started within 1 h of submitting the entry (enforced server-side in
// NinjaCutController::start). Mirror that window client-side: auto-hide the stale modal after 1 h,
// unless the real game has already begun (the timer is cleared on game start).
const MODAL_AUTO_HIDE_MS = 60 * 60 * 1000;
// The game must also be STARTED before dailyEndHour (again enforced in NinjaCutController::start).
// If the modal opens this close to closing time, arm a timer that switches it to the
// "outside play hours" screen the moment the hour ticks over — otherwise the player would sit on
// the start screen unaware and only learn from the 403 after pressing Start.
const OUTSIDE_HOURS_WATCH_MS = 30 * 60 * 1000;

let _data = null; // submit payload — feeds the registered-data summary on the thanks screen
let _uuid = null;
let _score = null;
let _reason = null;
let _demoAttempts = 0;
let _autoHideTimer = null;
let _outsideHoursTimer = null;
let _hudPoints = null;
let _hudRaf = null;

const $modal = () => $(MODAL_SELECTOR);
const csrf = () => $modal().data('game-csrf');
const endpoint = (action) => url(`/api/application/${_uuid}/${action}`);

function show(screen) {
    const $m = $modal();
    $m.find('.modal-screen').addClass('d-none');
    $m.find(`.modal-screen[data-screen="${screen}"]`).removeClass('d-none');
    $m.find('.modal-dev-screen').val(screen);
}

/**
 * Write `points` into the top-left HUD, which is repeated on every screen (see
 * modals/application-hud.html.twig). Off-game screens show 0; during play the value is
 * mirrored from the engine each frame, hence the no-op on an unchanged score.
 */
function setHudPoints(points) {
    const value = points ?? 0;
    if (value === _hudPoints) {
        return;
    }
    _hudPoints = value;
    $modal().find('.modal-hud__value').text(value);
}

/** Mirror the engine's live score into the HUD until stopHudTracking() is called. */
function trackHudPoints() {
    stopHudTracking();
    const tick = () => {
        setHudPoints(getNinjaCutScore());
        _hudRaf = requestAnimationFrame(tick);
    };
    _hudRaf = requestAnimationFrame(tick);
}

/** Stop mirroring — must run before the HUD is set to a final value, or the loop overwrites it. */
function stopHudTracking() {
    if (_hudRaf !== null) {
        cancelAnimationFrame(_hudRaf);
        _hudRaf = null;
    }
}

/** Entry point: called from the form submit-success handler. */
export function startApplicationModal(data) {
    _data = data;
    _uuid = data.uuid;
    _score = null;
    _reason = null;
    _demoAttempts = 0;
    setHudPoints(0);

    // The submit handler suppresses ajax-form's post-success reset (see application.js), so do
    // it here: the native reset clears the fields AND fires the 'reset' event that mints a fresh
    // idempotency key (see form/idempotency.js), so a follow-up entry isn't rejected as a dupe.
    resetApplicationForm();

    $modal().modal('show');
    armAutoHide();
    armOutsideHoursWatch();
    show('confirmation');

    // No loading screen: warm the game assets in the background instead. Reading the
    // confirmation + two instruction screens takes far longer than the preload, so the sprites
    // are cached well before the canvas mounts (and the engine loads them itself regardless).
    preloadNinjaCutAssets();
}

/** Arm the 1 h auto-hide: once the start window passes, show the "start expired" screen on the
 *  (unstarted) modal — matching what the server would return if the user tried to start now. */
function armAutoHide() {
    clearAutoHide();
    _autoHideTimer = setTimeout(() => {
        _autoHideTimer = null;
        show('start-expired');
    }, MODAL_AUTO_HIDE_MS);
}

/** Cancel the auto-hide timer (e.g. once the real game has started, or the modal closed). */
function clearAutoHide() {
    if (_autoHideTimer !== null) {
        clearTimeout(_autoHideTimer);
        _autoHideTimer = null;
    }
}

/**
 * Arm the closing-hour watch, but only when the modal opened inside the last
 * OUTSIDE_HOURS_WATCH_MS before dailyEndHour — earlier than that and the 1 h start window runs
 * out first (armAutoHide takes over). On fire: tear down whatever is on screen (only the trial
 * game can be running — the real game clears this timer) and show "outside play hours".
 *
 * The closing moment arrives as an absolute epoch timestamp rendered server-side (see
 * StageProvider::getDailyCloseAt), NOT as an hour: the server checks play hours in APP_TIMEZONE,
 * so reconstructing the hour in browser-local time would fire at the wrong moment for anyone in
 * another timezone. Only the remaining duration is measured here, which is timezone-agnostic.
 */
function armOutsideHoursWatch() {
    clearOutsideHoursWatch();

    const closesAtMs = Number($modal().data('daily-closes-at'));
    if (!Number.isFinite(closesAtMs) || closesAtMs <= 0) {
        return;
    }

    const msUntilClose = closesAtMs - Date.now();

    // Already past closing time (the server would have rejected the entry itself), or too far
    // out for this watch to be the binding limit.
    if (msUntilClose <= 0 || msUntilClose > OUTSIDE_HOURS_WATCH_MS) {
        return;
    }

    _outsideHoursTimer = setTimeout(() => {
        _outsideHoursTimer = null;
        teardownNinjaCut();
        stopHudTracking();
        setHudPoints(0);
        show('outside-hours');
    }, msUntilClose);
}

/** Cancel the closing-hour watch (once the real game has started, or the modal closed). */
function clearOutsideHoursWatch() {
    if (_outsideHoursTimer !== null) {
        clearTimeout(_outsideHoursTimer);
        _outsideHoursTimer = null;
    }
}

/** Reset the underlying page form; its native 'reset' event regenerates the idempotency key. */
function resetApplicationForm() {
    $('.application-form').each(function () {
        this.reset();
    });
}

function gameContainer() {
    return $modal().find('#game-canvas-container').get(0);
}

/** Real game: the score is registered server-side by /ninja-cut/result; here we reveal the result. */
function startGame() {
    // The game is starting → the start window and the closing hour no longer apply (a started
    // game may finish after dailyEndHour); stop both timers so nothing interrupts the play.
    clearAutoHide();
    clearOutsideHoursWatch();
    show('game');
    setHudPoints(0);
    trackHudPoints();
    startNinjaCut(gameContainer(), {
        demo: false,
        uuid: _uuid,
        onDone: function (score, reason) {
            _score = score;
            _reason = reason;
            stopHudTracking();
            renderResult();
            show(resultScreen());
        },
        onError: function (error) {
            stopHudTracking();
            // /start can reject with a code we map to a dedicated screen; anything else falls
            // back to a neutral score-0 result.
            const code = error && error.data && error.data.error;
            if (code === 'outside_hours') {
                setHudPoints(0);
                return showOutsideHours(error.data);
            }
            if (code === 'start_window_expired') {
                setHudPoints(0);
                return show('start-expired');
            }
            _score = 0;
            _reason = null; // an error is not a time-out — always the plain result screen
            renderResult();
            show(resultScreen());
        },
    });
}

/** Fill the "outside play hours" screen with the configured window and reveal it. */
function showOutsideHours(data) {
    const start = data && data.startHour != null ? data.startHour : null;
    const end = data && data.endHour != null ? data.endHour : null;
    if (start != null && end != null) {
        $modal().find('.outside-hours-range').text(`${start}:00–${end}:00`);
    }
    show('outside-hours');
}

/** Trial game (up to three attempts): score is discarded; finishing advances to the trial-result screen. */
function startDemo() {
    if (_demoAttempts >= MAX_DEMO_ATTEMPTS) {
        return;
    }
    show('game');
    setHudPoints(0);
    trackHudPoints();
    startNinjaCut(gameContainer(), {
        demo: true,
        onDone: function () {
            _demoAttempts++;
            stopHudTracking();
            renderDemoResult();
            show('demo-result');
        },
    });
}

/** Update the trial-result screen for the just-finished attempt. */
function renderDemoResult() {
    const $m = $modal();
    const left = Math.max(0, MAX_DEMO_ATTEMPTS - _demoAttempts);

    // Whole sentence, not just the number: Polish needs the verb to agree with the count
    // ("Została Ci 1 próba" vs "Zostały Ci 2 próby"). Hidden once the trials run out.
    $m.find('.demo-attempts-left')
        .text(left === 1 ? 'Została Ci 1 próba' : `Zostały Ci ${left} próby`)
        .toggleClass('d-none', left === 0);

    // Offer another attempt only while the user has trials left.
    $m.find('.demo-again').toggleClass('d-none', left === 0);

    // The trial score does not count, so the HUD goes back to zero.
    setHudPoints(0);
}

/** The score lives in the top-left HUD; both result screens are otherwise static copy. */
function renderResult() {
    setHudPoints(_score ?? 0);
}

/** Running out of time gets its own copy of the result screen; losing all lives uses the plain one. */
function resultScreen() {
    return _reason === 'timeup' ? 'timeout-result' : 'result';
}

/**
 * Reveal the closing screen. It carries the registered-data summary and the "print
 * confirmation" link, both filled from the submit payload (the link's href comes from
 * `data.pdf` — see PersonalDataFormController).
 *
 * The submit payload predates the game, so the answer endpoint hands back the score and the
 * answer (see saveApplicationAnswer) and they are folded in here — the same two values the
 * printed PDF shows.
 */
function showThanks(extra = null) {
    if (extra) {
        _data = { ..._data, ...extra };
    }
    if (_data) {
        setRegisteredData(_data, MODAL_SELECTOR);
    }
    show('thanks');
    // Ankieta wjeżdża nad ten ekran chwilę później — daje czas na przeczytanie potwierdzenia.
    armQuestionnaire(_uuid);
}

/** Resign from the contest question: run the DECLINE transition and show the thank-you screen. */
function resign(button) {
    const $button = $(button).prop('disabled', true);

    $.ajax({
        type: 'POST',
        url: endpoint('answer'),
        data: { decline: 1, _token: csrf() },
    }).done(function (response) {
        showThanks(response);
    }).fail(function () {
        alert('Nie udało się zapisać rezygnacji. Spróbuj ponownie.');
    }).always(function () {
        $button.prop('disabled', false);
    });
}

function submitAnswer(decline) {
    const $m = $modal();
    const $textarea = $m.find('.quest-answer');
    const $error = $m.find('.quest-answer-error');
    const $buttons = $m.find('[data-modal-action="answer"], [data-modal-action="decline"]');
    const answer = ($textarea.val() || '').trim();

    $error.text('').hide();
    $textarea.removeClass('is-invalid');

    if (!decline && answer === '') {
        $textarea.addClass('is-invalid');
        $error.text('Wpisz odpowiedź').show();
        return;
    }

    $buttons.prop('disabled', true);

    $.ajax({
        type: 'POST',
        url: endpoint('answer'),
        data: { answer: answer, decline: decline ? 1 : 0, _token: csrf() },
    }).done(function (response) {
        showThanks(response);
    }).fail(function (xhr) {
        const message = xhr.responseJSON?.formErrors?.answer?.[0]
            || xhr.responseJSON?.errors?.[0]
            || 'Nie udało się zapisać odpowiedzi. Spróbuj ponownie.';
        $textarea.addClass('is-invalid');
        $error.text(message).show();
    }).always(function () {
        $buttons.prop('disabled', false);
    });
}

// The modal owns the whole viewport, so lock page scrolling while it is up (see the
// .game-modal-open rules in s-game-modal.scss).
$(document).on('show.bs.modal', MODAL_SELECTOR, function () {
    $('html').addClass('game-modal-open');
});

// Stop the game, release the scroll lock and cancel the pending timers whenever the modal
// is closed.
$(document).on('hidden.bs.modal', MODAL_SELECTOR, function () {
    $('html').removeClass('game-modal-open');
    stopHudTracking();
    clearAutoHide();
    clearOutsideHoursWatch();
    clearQuestionnaire();
    teardownNinjaCut();
});

// DEV ONLY: jump to the chosen screen (renders the result score / trial HUD on the way).
$(document).on('change', `${MODAL_SELECTOR} .modal-dev-screen`, function () {
    const screen = $(this).val();
    teardownNinjaCut();
    stopHudTracking();
    // Bez tego ankieta uzbrojona na `thanks` wyskoczyłaby nad ekranem, na który się przeskoczyło.
    clearQuestionnaire();
    setHudPoints(0);
    if (screen === 'result' || screen === 'timeout-result') {
        renderResult();
    }
    if (screen === 'demo-result') {
        renderDemoResult();
    }
    if (screen === 'thanks') {
        return showThanks();
    }
    show(screen);
});

// DEV ONLY: open the modal with a stub payload + hardcoded uuid, skipping the form submit.
$(document).on('click', '.application-modal-dev', function () {
    startApplicationModal({
        status: 'OK',
        uuid: $(this).data('dev-uuid'),
        firstname: 'Jan',
        lastname: 'Kowalski',
        email: 'jan.kowalski@example.com',
        phone: '500600700',
        billType: 'Paragon',
        billNr: '0001',
        billDate: '01.01.2026',
        billNip: null,
        billPos: '01',
        prize: null,
        formUrl: null,
    });
});

$(document).on('click', `${MODAL_SELECTOR} [data-modal-next]`, function () {
    show($(this).data('modal-next'));
});

$(document).on('click', `${MODAL_SELECTOR} [data-modal-action]`, function () {
    switch ($(this).data('modal-action')) {
        case 'start-game':
            return startGame();
        case 'start-demo':
            return startDemo();
        case 'resign':
            return resign(this);
        case 'answer':
            return submitAnswer(false);
        case 'decline':
            return submitAnswer(true);
    }
});
